import {
  clearOnboardingLiveSession,
  finishOnboarding,
  getOnboardingIsNew,
  getOnboardingState,
  isOnboardingLiveSession,
  markOnboardingLiveSession,
  patchOnboardingState,
  type OnboardingSpot,
  type OnboardingState,
} from "./selfSetupStorage";

export type SelfSetupTourStep = "supply" | "dashboard" | "orders";

/** Welcome hub steps for Market (recycler chrome). */
export const SELF_SETUP_TOUR_ORDER: SelfSetupTourStep[] = [
  "supply",
  "dashboard",
  "orders",
];

export function getSelfSetupTourOrder(
  _organizationType?: string | null,
): SelfSetupTourStep[] {
  return SELF_SETUP_TOUR_ORDER;
}

/** Sub-phases within an active tour step. */
export type SelfSetupTourPhase =
  | "nav"
  | "period"
  | "lines"
  | "save"
  | "periodToggle"
  | "fulfillment"
  | "summary"
  | "filters"
  | "list"
  | "createButton"
  | "create"
  | "acceptsExtra"
  | "fulfillmentTable"
  | "dashboardOrders";

export const SUPPLY_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "period",
  "lines",
  "acceptsExtra",
  "save",
];

export const DASHBOARD_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "periodToggle",
  "fulfillment",
  "fulfillmentTable",
  "dashboardOrders",
];

export const ORDERS_TOUR_PHASES: SelfSetupTourPhase[] = [
  "nav",
  "summary",
  "filters",
  "list",
  "createButton",
  "create",
];

function phasesForStep(step: SelfSetupTourStep): SelfSetupTourPhase[] {
  if (step === "supply") return SUPPLY_TOUR_PHASES;
  if (step === "dashboard") return DASHBOARD_TOUR_PHASES;
  return ORDERS_TOUR_PHASES;
}

function normalizeStep(raw: string | null | undefined): SelfSetupTourStep | null {
  if (!raw) return null;
  return (SELF_SETUP_TOUR_ORDER as string[]).includes(raw)
    ? (raw as SelfSetupTourStep)
    : null;
}

function normalizePhase(
  step: SelfSetupTourStep,
  raw: string | null | undefined,
): SelfSetupTourPhase {
  const allowed = phasesForStep(step);
  if (raw && (allowed as string[]).includes(raw)) {
    return raw as SelfSetupTourPhase;
  }
  return "nav";
}

function filterCompleted(completed: string[]): SelfSetupTourStep[] {
  const order = getSelfSetupTourOrder();
  const normalized = completed
    .map((s) => normalizeStep(s))
    .filter((s): s is SelfSetupTourStep => s != null);
  return order.filter((s) => normalized.includes(s));
}

function readState(userId: string, organizationId: string): OnboardingState {
  return getOnboardingState(userId, organizationId);
}

export function finishSelfSetupTour(
  userId: string,
  organizationId: string,
): void {
  finishOnboarding(userId, organizationId);
}

export function getActiveSelfSetupTourStep(
  userId: string,
  organizationId: string,
): SelfSetupTourStep | null {
  const { page, spot } = readState(userId, organizationId);
  if (!page || spot === "hub" || spot === "congrats") return null;
  return normalizeStep(page);
}

export function setActiveSelfSetupTourStep(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep,
): void {
  patchOnboardingState(userId, organizationId, {
    page: step,
    spot: "nav",
    resumePage: step,
    resumeSpot: "nav",
  });
  markOnboardingLiveSession();
}

export function pauseOnboardingAtTip(
  userId: string,
  organizationId: string,
  page: SelfSetupTourStep,
  spot: SelfSetupTourPhase,
): void {
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: "hub",
    resumePage: page,
    resumeSpot: spot,
  });
  clearOnboardingLiveSession();
}

export function clearActiveSelfSetupTourStep(
  userId: string,
  organizationId: string,
): void {
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: "hub",
    resumePage: null,
    resumeSpot: undefined,
  });
  clearOnboardingLiveSession();
}

export function getSelfSetupTourPhase(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep | null = getActiveSelfSetupTourStep(
    userId,
    organizationId,
  ),
): SelfSetupTourPhase {
  if (!step) return "nav";
  const { spot } = readState(userId, organizationId);
  if (spot === "hub" || spot === "congrats") return "nav";
  return normalizePhase(step, spot);
}

export function setSelfSetupTourPhase(
  userId: string,
  organizationId: string,
  phase: SelfSetupTourPhase,
): void {
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) return;
  patchOnboardingState(userId, organizationId, {
    page: step,
    spot: phase,
    resumePage: step,
    resumeSpot: phase,
  });
  markOnboardingLiveSession();
}

export function getCompletedSelfSetupSteps(
  userId: string,
  organizationId: string,
  _organizationType?: string | null,
): SelfSetupTourStep[] {
  const { completed } = readState(userId, organizationId);
  return filterCompleted(completed);
}

export function markSelfSetupStepCompleted(
  userId: string,
  organizationId: string,
  step: SelfSetupTourStep,
  organizationType?: string | null,
): SelfSetupTourStep[] {
  const current = getCompletedSelfSetupSteps(
    userId,
    organizationId,
    organizationType,
  );
  if (current.includes(step)) return current;
  const next = [...current, step];
  const stillIncomplete = getFirstIncompleteSelfSetupStep(next);
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: stillIncomplete ? "hub" : "congrats",
    completed: next,
    resumePage: null,
    resumeSpot: undefined,
  });
  clearOnboardingLiveSession();
  return getCompletedSelfSetupSteps(userId, organizationId, organizationType);
}

export function getFirstIncompleteSelfSetupStep(
  completed: SelfSetupTourStep[],
  _organizationType?: string | null,
): SelfSetupTourStep | null {
  return getSelfSetupTourOrder().find((s) => !completed.includes(s)) ?? null;
}

export function getNextTourPhase(
  step: SelfSetupTourStep,
  phase: SelfSetupTourPhase,
): SelfSetupTourPhase | null {
  const order = phasesForStep(step);
  const i = order.indexOf(phase);
  if (i < 0 || i >= order.length - 1) return null;
  return order[i + 1];
}

export function getSelfSetupTourPath(
  step: SelfSetupTourStep,
  _organizationId: string,
): { path: string; state?: Record<string, unknown> } {
  switch (step) {
    case "supply":
      return { path: "/supply", state: { selfSetupTour: "supply" } };
    case "dashboard":
      return { path: "/dashboard", state: { selfSetupTour: "dashboard" } };
    case "orders":
      return { path: "/orders", state: { selfSetupTour: "orders" } };
  }
}

export function isSelfSetupTourPath(
  step: SelfSetupTourStep,
  pathname: string,
): boolean {
  switch (step) {
    case "supply":
      return pathname === "/supply";
    case "dashboard":
      return pathname === "/dashboard";
    case "orders":
      return pathname === "/orders";
  }
}

export function pauseOnboardingAtHub(
  userId: string,
  organizationId: string,
): void {
  patchOnboardingState(userId, organizationId, {
    page: null,
    spot: "hub",
    resumePage: null,
    resumeSpot: undefined,
  });
  clearOnboardingLiveSession();
}

export function parkActiveOnboardingOnLogout(
  userId: string,
  organizationId: string,
): void {
  if (!userId || !organizationId) return;
  if (!getOnboardingIsNew(userId, organizationId)) return;
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) return;
  const spot = getSelfSetupTourPhase(userId, organizationId, step);
  pauseOnboardingAtTip(userId, organizationId, step, spot);
}

export function parkAllActiveOnboardingForUser(userId: string): void {
  if (!userId || typeof localStorage === "undefined") return;
  const prefix = "market.onboarding_state.";
  const userPrefix = `${prefix}${userId}.`;
  const orgIds: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith(userPrefix)) continue;
    const orgId = key.slice(userPrefix.length);
    if (orgId) orgIds.push(orgId);
  }
  for (const orgId of orgIds) {
    parkActiveOnboardingOnLogout(userId, orgId);
  }
}

export function parkAllLiveOnboardingTips(): void {
  if (typeof localStorage === "undefined") return;
  const prefix = "market.onboarding_state.";
  const pairs: { userId: string; orgId: string }[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith(prefix)) continue;
    const rest = key.slice(prefix.length);
    const lastDot = rest.lastIndexOf(".");
    if (lastDot <= 0) continue;
    const userId = rest.slice(0, lastDot);
    const orgId = rest.slice(lastDot + 1);
    if (userId && orgId) pairs.push({ userId, orgId });
  }
  for (const { userId, orgId } of pairs) {
    parkActiveOnboardingOnLogout(userId, orgId);
  }
  clearOnboardingLiveSession();
}

export function recoverOrphanLiveOnboardingTip(
  userId: string,
  organizationId: string,
): boolean {
  if (!userId || !organizationId) return false;
  if (isOnboardingLiveSession()) return false;
  const step = getActiveSelfSetupTourStep(userId, organizationId);
  if (!step) return false;
  parkActiveOnboardingOnLogout(userId, organizationId);
  return true;
}

export function getOnboardingResumePoint(
  userId: string,
  organizationId: string,
): { page: SelfSetupTourStep; spot: SelfSetupTourPhase } | null {
  const state = readState(userId, organizationId);

  const resumePage = normalizeStep(state.resumePage ?? null);
  if (resumePage) {
    return {
      page: resumePage,
      spot: normalizePhase(resumePage, state.resumeSpot),
    };
  }

  const livePage = normalizeStep(state.page);
  if (livePage && state.spot !== "hub" && state.spot !== "congrats") {
    return {
      page: livePage,
      spot: normalizePhase(livePage, state.spot),
    };
  }

  return null;
}

export function activateOnboardingResume(
  userId: string,
  organizationId: string,
): { page: SelfSetupTourStep; spot: SelfSetupTourPhase } | null {
  const resume = getOnboardingResumePoint(userId, organizationId);
  if (!resume) return null;
  patchOnboardingState(userId, organizationId, {
    page: resume.page,
    spot: resume.spot,
    resumePage: resume.page,
    resumeSpot: resume.spot,
  });
  markOnboardingLiveSession();
  return resume;
}

export function isOnboardingCongratsPending(
  userId: string,
  organizationId: string,
): boolean {
  return readState(userId, organizationId).spot === "congrats";
}

export type { OnboardingSpot, OnboardingState };
